import React, { useMemo, useState } from 'react';
import { inferGender, serviceCategory, serviceKey } from '../../utils/services';
import { formatMoney } from '../../utils/money';
import { CheckIcon, SearchIcon } from '../common/icons';
import { EmptyState, Loader } from '../common/Feedback';

const GENDERS = [
  { id: 'all', label: 'All' },
  { id: 'women', label: '♀ Women' },
  { id: 'men', label: '♂ Men' },
];

/** Branch price list: Men / Women, search, categories, tap a card to add or remove it. */
export default function ServiceCatalog({ services, loading, error, selectedKeys, onToggle }) {
  const [gender, setGender] = useState('all');
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');

  const enriched = useMemo(
    () => services.map((s) => ({ ...s, _gender: inferGender(s), _category: serviceCategory(s) })),
    [services],
  );
  const byGender = enriched.filter((s) => gender === 'all' || s._gender === gender || s._gender === 'unisex');
  const categories = ['all', ...Array.from(new Set(byGender.map((s) => s._category))).sort((a, b) => a.localeCompare(b))];
  const activeCategory = categories.includes(category) ? category : 'all';
  const needle = q.trim().toLowerCase();
  const shown = byGender.filter(
    (s) =>
      (needle ? s.name.toLowerCase().includes(needle) : activeCategory === 'all' || s._category === activeCategory),
  );

  if (loading) return <Loader text="Loading the branch price list…" />;
  if (error) return <EmptyState title="Price list unavailable" body={error} />;
  if (!services.length) return <EmptyState title="No services for this branch" body="This branch has no price list in Nyafaz yet." />;

  return (
    <div className="dp-catalog">
      <div className="dp-catalog-bar">
        <div className="dp-segmented" role="tablist">
          {GENDERS.map((g) => (
            <button
              key={g.id}
              type="button"
              className={gender === g.id ? 'is-on' : ''}
              onClick={() => {
                setGender(g.id);
                setCategory('all');
              }}
            >
              {g.label}
            </button>
          ))}
        </div>
        <div className="dp-search">
          <SearchIcon size={16} />
          <input placeholder={`Search ${services.length} services`} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {!needle ? (
        <div className="dp-chips dp-chips--scroll">
          {categories.map((c) => (
            <button key={c} type="button" className={`dp-chip-btn${activeCategory === c ? ' is-on' : ''}`} onClick={() => setCategory(c)}>
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>
      ) : null}

      {shown.length ? (
        <div className="dp-svc-grid">
          {shown.map((s) => {
            const on = selectedKeys.has(serviceKey(s.name));
            return (
              <button key={s.id || s.name} type="button" className={`dp-svc-card${on ? ' is-on' : ''}`} onClick={() => onToggle(s)} aria-pressed={on}>
                <span className="dp-svc-check">{on ? <CheckIcon size={14} /> : null}</span>
                <span className="dp-svc-name">{s.name}</span>
                <span className="dp-svc-tags">
                  {s._gender !== 'unisex' ? <span className={`dp-tag dp-tag--${s._gender}`}>{s._gender === 'men' ? 'Men' : 'Women'}</span> : null}
                  <span className="dp-tag">{s._category}</span>
                </span>
                <span className="dp-svc-price">
                  {s.unit_price != null ? formatMoney(s.unit_price) : s.price_text || 'Price on request'}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <EmptyState title="No services match" body={needle ? 'Try another word.' : 'Pick another category.'} />
      )}
    </div>
  );
}
